import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  Form,
  TextField,
  Select,
  Button,
  Toast,
  ChoiceList,
  Checkbox,
  Banner,
  List,
  TextContainer,
  FormLayout,
  BlockStack,
  InlineStack,
  Box,
  ButtonGroup,
  Text,
  SkeletonBodyText,
  Tag,Frame
} from '@shopify/polaris';
import { PlusIcon } from '@shopify/polaris-icons';
import { SaveBar, useAppBridge } from '@shopify/app-bridge-react';
import useAxiosClient from '../axiosClient';
import { useNavigate, useParams } from 'react-router-dom';
import { SortableList } from "../SortableList";
import { useTranslation } from 'react-i18next';
import '../../i18n';

export default function EditTasklist() {
  const navigate = useNavigate();
  const { id } = useParams(); // Get task list ID from URL
  const axiosClient = useAxiosClient();
  const appBridge = useAppBridge();
  const { t } = useTranslation();
  
  // State for task list with assignedTo as array
  const [taskListInfo, setTaskListInfo] = useState({
    name: '',
    description: '',
    assignedTo: [], // Changed to array for multi-select
    selectedLocationType: 'All',
    selectedLocations: [],
    frequency: {
      daily: false,
      weekly: false,
      monthly: false
    },
  });

  // Available assign to options
  const assignToOptions = [
    // { label: 'Owner', value: 'Owner' },
    { label: t('staff'), value: 'Staff' },
    { label: t('manager'), value: 'Manager' },
  ];

  // State for tasks
  const [tasks, setTasks] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [showTaskInput, setShowTaskInput] = useState(false);
  const [taskInputError, setTaskInputError] = useState('');

  const [locations, setLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [isLoadingTasklist, setIsLoadingTasklist] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastContent, setToastContent] = useState('');
  const [errors, setErrors] = useState({});
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const originalData = useRef(null);

  // Update showErrorBanner whenever errors change
  useEffect(() => {
    setShowErrorBanner(Object.keys(errors).length > 0);
  }, [errors]);
  
  // Fetch tasklist data
  useEffect(() => {
    const fetchTasklistData = async () => {
      try {
        setIsLoadingTasklist(true);
        const response = await axiosClient.get(`/tasklists/${id}`);
        
        if (response.data.success) {
          const tasklistData = response.data.data;
          
          // Process timeline to set frequency checkboxes
          const timeline = tasklistData.timeline ? tasklistData.timeline.split(',') : [];
          const frequencyData = {
            daily: timeline.includes('daily'),
            weekly: timeline.includes('weekly'),
            monthly: timeline.includes('monthly')
          };
          
          // Process locations
          const locationType = tasklistData.locations ? 'Specific' : 'All';
          const locationsList = locationType === 'Specific' 
            ? tasklistData.locations.split(', ') // Split locations string into array
            : [];
          
          // Process assignees - split if it contains commas
          const assignees = tasklistData.assigned_to ? 
            tasklistData.assigned_to.split(', ').map(item => item.trim()) : 
            [];
          
          // Set tasklist info state
          const taskListFormData = {
            name: tasklistData.name || '',
            description: tasklistData.description || '',
            assignedTo: assignees, // Now an array
            selectedLocationType: locationType,
            selectedLocations: locationsList,
            frequency: frequencyData,
          };
          
          setTaskListInfo(taskListFormData);
          
          // Process tasks
          const taskItems = tasklistData.tasks.map(task => ({
            id: task.id,
            title: task.task_name,
            status: task.status || 'active'
          }));
          
          setTasks(taskItems);
          
          // Store original data for comparison/reset
          originalData.current = {
            taskListInfo: {...taskListFormData},
            tasks: [...taskItems]
          };
        } else {
          setToastContent(t('failed_to_load_task_list_data'));
          setShowToast(true);
        }
      } catch (error) {
        setToastContent(t('error_loading_task_list'));
        setShowToast(true);
      } finally {
        setIsLoadingTasklist(false);
      }
    };

    fetchTasklistData();
  }, [id]);
  
  // Handle adding a new task
  const handleAddTask = () => {
    if (!newTaskTitle.trim()) {
      setTaskInputError(t('task_title_cannot_be_empty'));
      return;
    }
    
    // Create new task with a unique ID
    const newTask = {
      id: `new-task-${Date.now()}`, // Temporary ID for new tasks
      title: newTaskTitle.trim(),
      status: 'active',
      isNew: true // Flag to indicate this is a new task
    };
    
    // Add to task list
    setTasks([...tasks, newTask]);
    setNewTaskTitle('');
    setShowTaskInput(false);
    setTaskInputError('');
    setIsDirty(true);
    
    // Clear the tasks error if it exists
    if (errors.tasks) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.tasks;
        return newErrors;
      });
    }
  };
  
  // Track input changes and mark form as dirty
  const handleChange = (field) => (value) => {
    setTaskListInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);
    
    // Clear the error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle assign to checkbox changes
  const handleAssignToChange = (selectedValues) => {
    setTaskListInfo(prev => ({
      ...prev,
      assignedTo: selectedValues
    }));
    setIsDirty(true);
    
    // Clear error for assignedTo if it exists
    if (errors.assignedTo) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.assignedTo;
        return newErrors;
      });
    }
  };

  // Remove an assignee tag
  const removeAssignee = (assigneeToRemove) => {
    setTaskListInfo(prev => ({
      ...prev,
      assignedTo: prev.assignedTo.filter(assignee => assignee !== assigneeToRemove)
    }));
    setIsDirty(true);
  };

  // Handle frequency checkboxes
  const handleFrequencyChange = (key) => (checked) => {
    let newFrequency;
    
    if (checked) {
      // If this checkbox is being checked, uncheck all others
      newFrequency = {
        daily: key === 'daily',
        weekly: key === 'weekly',
        monthly: key === 'monthly'
      };
      setTaskListInfo((prev) => ({
        ...prev,
        frequency: newFrequency
      }));
    } else {
      // If this checkbox is being unchecked, just uncheck it
      newFrequency = {
        daily: key === 'daily' ? false : taskListInfo.frequency.daily,
        weekly: key === 'weekly' ? false : taskListInfo.frequency.weekly,
        monthly: key === 'monthly' ? false : taskListInfo.frequency.monthly
      };
      setTaskListInfo((prev) => ({
        ...prev,
        frequency: newFrequency
      }));
    }
    
    setIsDirty(true);
    
    // Clear frequency error if any frequency is selected
    if ((newFrequency.daily || newFrequency.weekly || newFrequency.monthly) && errors.frequency) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.frequency;
        return newErrors;
      });
    }
    
    // Add frequency error back if no frequency is selected
    if (!newFrequency.daily && !newFrequency.weekly && !newFrequency.monthly) {
      setErrors((prev) => ({
        ...prev,
        frequency: t('please_select_at_least_one_frequency_option')
      }));
    }
  };
  
  // Handle task deletion
  const handleDeleteTask = (taskId) => {
    const updatedTasks = tasks.filter(task => task.id !== taskId);
    setTasks(updatedTasks);
    setIsDirty(true);
    
    // If all tasks are deleted, add the tasks error back
    if (updatedTasks.length === 0) {
      setErrors((prev) => ({
        ...prev,
        tasks: t('please_add_at_least_one_task_to_the_task_list')
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Task List Validation
    if (!taskListInfo.name.trim()) {
      newErrors.name = t('task_list_name_is_required');
    } else if (taskListInfo.name.length < 2) {
      newErrors.name = t('task_list_name_must_be_at_least_2_characters');
    }
    
    // Assign To Validation
    if (!taskListInfo.assignedTo || taskListInfo.assignedTo.length === 0) {
      newErrors.assignedTo = t('please_select_at_least_one_assignee');
    }
    
    // Location Validation
    if (taskListInfo.selectedLocationType === 'Specific' && 
        (!taskListInfo.selectedLocations || taskListInfo.selectedLocations.length === 0)) {
      newErrors.locations = t('please_select_at_least_one_location');
    }
    
    // Frequency Validation
    const hasFrequency = Object.values(taskListInfo.frequency).some(value => value);
    if (!hasFrequency) {
      newErrors.frequency = t('please_select_at_least_one_frequency_option');
    }
    
    // Tasks Validation
    if (tasks.length === 0) {
      newErrors.tasks = t('please_add_at_least_one_task_to_the_task_list');
    }
    
    // Set the errors and return validation result
    setErrors(newErrors);
    
    // If there are any errors, show a toast message
    // if (Object.keys(newErrors).length > 0) {
    //   setShowToast(true);
    //   setToastContent('Please fill in all required fields correctly');
    // }
    
    return Object.keys(newErrors).length === 0;
  };

  const valueRef = useRef({
    taskListInfo,
    tasks
  });
  
  useEffect(() => {
    valueRef.current = {
      taskListInfo,
      tasks
    };
  }, [taskListInfo, tasks]);

  const handleSave = async () => {
    if (!validateForm()) {
      // setShowToast(true);
      // setToastContent('Please fix the validation errors before saving');
      setShowErrorBanner(true);
      appBridge.saveBar.hide('edit-tasklist-save-bar');
      return;
    }
    
    try {
      setIsSaving(true);
      const allLocationNames = locations.map(location => location.name).join(', ');
      
      // Convert assignedTo array to string for API
      const assignedToString = valueRef.current.taskListInfo.assignedTo.join(', ');
      
      // Prepare data for update
      const dataToSend = {
        name: valueRef.current.taskListInfo.name,
        description: valueRef.current.taskListInfo.description,
        assignedTo: assignedToString, // Send as comma-separated string
        locations: valueRef.current.taskListInfo.selectedLocationType === 'All' 
          ? allLocationNames
          : valueRef.current.taskListInfo.selectedLocations.join(', '),
        frequency: valueRef.current.taskListInfo.frequency,
        tasks: valueRef.current.tasks.map(task => ({
          id: task.id && !task.id.toString().includes('new-task') ? task.id : null, // Only send real IDs, not temp ones
          title: task.title,
          status: task.status,
          isNew: task.isNew || false
        }))
      };
      
      // Send update request
      const response = await axiosClient.post(`/update-tasklist/${id}`, dataToSend);
      
      if (response.data.success) {
        setIsDirty(false);
        shopify.toast.show(t('task_list_updated'));
        
        // Update originalData with the current state to reset dirty state
        originalData.current = {
          taskListInfo: {...taskListInfo},
          tasks: [...tasks]
        };
        appBridge.saveBar.hide('edit-tasklist-save-bar');
        // Navigate back to task list page
        navigate('/Tasklist');
      } else {
        // Handle server validation errors
        if (response.data.errors) {
          setErrors(response.data.errors);
          setShowErrorBanner(true);
          // Show first error message in toast
          const firstError = Object.values(response.data.errors)[0];
          shopify.toast.show(firstError[0] || t('validation_failed'), { error: true });
          appBridge.saveBar.hide('edit-tasklist-save-bar');
        } else {
          shopify.toast.show(response.data.message || t('failed_to_update_task_list'), { error: true });
          appBridge.saveBar.hide('edit-tasklist-save-bar');
        }
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        // Handle validation errors from server
        setErrors(error.response.data.errors);
        setShowErrorBanner(true);
        const firstError = Object.values(error.response.data.errors)[0];
        // shopify.toast.show(firstError[0] || 'Validation failed', { error: true });
        appBridge.saveBar.hide('edit-tasklist-save-bar');
      } else {
        setToastContent(t('error_updating_task_list'));
        // shopify.toast.show('Error updating task list', { error: true });
        appBridge.saveBar.hide('edit-tasklist-save-bar');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Discard changes and reset form to original data
  const handleDiscard = () => {
    if (originalData.current) {
      setTaskListInfo(originalData.current.taskListInfo);
      setTasks(originalData.current.tasks);
    }
    setIsDirty(false);
    setErrors({});
    setShowErrorBanner(false);
    appBridge.saveBar.hide('edit-tasklist-save-bar');
  };

  // Fetch locations for the form
  const fetchLocations = async () => {
    try {
      setIsLoadingLocations(true);
      const response = await axiosClient.get('/locations');
      if (response.data.success) {
        setLocations(response.data.locations);
      } else {
        setToastContent(t('failed_to_load_locations'));
        setShowToast(true);
      }
    } catch (error) {
      setToastContent(t('error_loading_locations'));
      setShowToast(true);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  // Fetch locations on component mount
  useEffect(() => {
    fetchLocations();
  }, []);


  const handleBackClick = useCallback(() => {
    if (isDirty) {
      appBridge.saveBar.leaveConfirmation()
        .then(() => {
          // User confirmed leaving or no changes to save
          navigate("/Tasklist");
        })
        .catch(() => {
          // User cancelled leaving
        });
    } else {
      // No unsaved changes, navigate directly
      navigate("/Tasklist");
    }
  }, [isDirty, appBridge, navigate]);
  
  // Function to dismiss the error banner
  const dismissErrorBanner = () => {
    setShowErrorBanner(false);
  };

  // Show loading state while data is being fetched
  if (isLoadingTasklist) {
    return (
      <Page
        title={t('edit_task_list')}
        fullWidth
        backAction={{
          content: t('task_lists'),
          onAction: handleBackClick 
        }}
      >
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <TextContainer>
                <SkeletonBodyText lines={3} />
              </TextContainer>
            </Card>
          </Layout.Section>
          
          <Layout.Section>
            <Card sectioned>
              <TextContainer>
                <SkeletonBodyText lines={2} />
              </TextContainer>
            </Card>
          </Layout.Section>
          
          <Layout.Section>
            <Card sectioned>
              <TextContainer>
                <SkeletonBodyText lines={5} />
              </TextContainer>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={t('edit_task_list')}
      fullWidth
      backAction={{
        content: t('task_lists'),
        onAction: handleBackClick
      }}
    >
      {showErrorBanner && Object.keys(errors).length > 0 && (
        <Box paddingBlockEnd="400">
          <Banner
            title={t('please_correct_the_following_errors')}
            tone="critical"
            onDismiss={dismissErrorBanner}
          >
            <List type="bullet">
              {Object.entries(errors).map(([field, messages]) => (
                <List.Item key={field}>
                  {`${field.charAt(0).toUpperCase() + field.slice(1)}: ${Array.isArray(messages) ? messages[0] : messages}`}
                </List.Item>
              ))}
            </List>
          </Banner>
        </Box>
      )}
      <div style={{marginTop:'10px'}}></div>

      <Form onSubmit={handleSave}>
        <Layout>
          {/* Task List Information */}
          <Layout.AnnotatedSection
            title={t('task_list_details')}
            description={t('update_your_task_list_name_and_description')}
          >
            <Card sectioned>
              <FormLayout>
                <TextField
                  label={t('task_list_name')}
                  value={taskListInfo.name}
                  onChange={handleChange('name')}
                  placeholder={t('enter_task_list_name')}
                  error={errors.name && Array.isArray(errors.name) ? errors.name[0] : errors.name}
                  autoComplete="off"
                  required
                />
                <TextField
                  label={t('description')}
                  value={taskListInfo.description}
                  onChange={handleChange('description')}
                  placeholder={t('describe_the_purpose_of_this_task_list')}
                  multiline={4}
                  error={errors.description}
                  autoComplete="off"
                />
              </FormLayout>
            </Card>
          </Layout.AnnotatedSection>

          {/* Assign To - Modified for multiple selection */}
          <Layout.AnnotatedSection
            title={t('assigned_to')}
            description={t('choose_who_should_be_responsible_for_completing_this_task_list')}
          >
            <Card sectioned>
              <BlockStack gap="400">
                {errors.assignedTo && (
                  <Banner tone="critical">{errors.assignedTo}</Banner>
                )}

                <ChoiceList
                  title={t('assign_to')}
                  allowMultiple
                  choices={assignToOptions}
                  selected={taskListInfo.assignedTo}
                  onChange={handleAssignToChange}
                />
                
                {/* Show selected assignees as tags */}
                {taskListInfo.assignedTo.length > 0 && (
                  <Box paddingBlockStart="300">
                    <TextContainer>
                      <Text variant="headingXs">{t('selected_assignees')}:</Text>
                      <InlineStack gap="200" wrap>
                        {taskListInfo.assignedTo.map(assignee => (
                          <Tag 
                            key={assignee}
                            onRemove={() => removeAssignee(assignee)}
                          >
                            {assignee}
                          </Tag>
                        ))}
                      </InlineStack>
                    </TextContainer>
                  </Box>
                )}
              </BlockStack>
            </Card>
          </Layout.AnnotatedSection>

          {/* Locations */}
          <Layout.AnnotatedSection
            title={t('locations')}
            description={t('select_the_locations_where_this_task_list_applies')}
          >
            <Card sectioned>
              <InlineStack align="start" gap="400">
                <ChoiceList
                  title=""
                  choices={[
                    { label: t('all_locations'), value: 'All' },
                    { label: t('specific_locations'), value: 'Specific' },
                  ]}
                  selected={[taskListInfo.selectedLocationType]}
                  onChange={(value) =>
                    setTaskListInfo((prev) => ({ 
                      ...prev, 
                      selectedLocationType: value[0],
                      selectedLocations: value[0] === 'All' ? [] : prev.selectedLocations 
                    }))
                  }
                />
              </InlineStack>
              
              {taskListInfo.selectedLocationType === 'Specific' && (
                <Box paddingBlockStart="400" paddingInline="400">
                  <TextContainer>
                    <p className="font-semibold">{t('available_locations')}</p>
                    {errors.locations && (
                      <Banner tone="critical">{errors.locations}</Banner>
                    )}
                  </TextContainer>
                  
                  {isLoadingLocations ? (
                    <Box paddingBlockStart="400">{t('loading_locations')}</Box>
                  ) : locations.length === 0 ? (
                    <Box paddingBlockStart="400">{t('no_locations_available')}</Box>
                  ) : (
                    <BlockStack gap="200">
                      {locations.map((location) => (
                        <Checkbox
                          key={location.id}
                          label={
                            <>
                              <strong>{location.name}</strong>
                              <div style={{ fontSize: '0.85rem', color: 'var(--p-text-subdued)' }}>
                                {location.address}
                              </div>
                            </>
                          }
                          checked={taskListInfo.selectedLocations.includes(location.name)}
                          onChange={(checked) => {
                            setTaskListInfo((prev) => ({
                              ...prev,
                              selectedLocations: checked
                                ? [...(prev.selectedLocations || []), location.name]
                                : (prev.selectedLocations || []).filter(name => name !== location.name)
                            }));
                            setIsDirty(true);
                          }}
                        />
                      ))}
                    </BlockStack>
                  )}
                </Box>
              )}
            </Card>
          </Layout.AnnotatedSection>

          {/* Frequency */}
          <Layout.AnnotatedSection
            title={t('timeline')}
            description={t('choose_how_frequently_this_task_list_should_be_completed')}
          >
            <Card sectioned>
              <TextContainer>
                {errors.frequency && (
                  <Box paddingBlockStart="300">
                    <Banner tone="critical">{errors.frequency}</Banner>
                  </Box>
                )}
              </TextContainer>
              
              <BlockStack gap="300" padding="400">
                <Checkbox
                  label={t('daily')}
                  checked={taskListInfo.frequency.daily}
                  onChange={handleFrequencyChange('daily')}
                  helpText={t('this_task_list_needs_to_be_completed_every_day')}
                />
                <Checkbox
                  label={t('weekly')}
                  checked={taskListInfo.frequency.weekly}
                  onChange={handleFrequencyChange('weekly')}
                  helpText={t('this_task_list_needs_to_be_completed_every_week')}
                />
                <Checkbox
                  label={t('monthly')}
                  checked={taskListInfo.frequency.monthly}
                  onChange={handleFrequencyChange('monthly')}
                  helpText={t('this_task_list_needs_to_be_completed_every_month')}
                />
              </BlockStack>
            </Card>
          </Layout.AnnotatedSection>

          {/* Task Management Section */}
          <Layout.AnnotatedSection
            title={t('tasks')}
            description={t('add_edit_and_arrange_the_tasks_that_need_to_be_completed')}
          >
            <Card sectioned>
              <BlockStack gap="400">
                <TextContainer>
                  <Text variant="headingSm">{t('task_items')}</Text>
                  <Text as="p" color="subdued">
                    {t('add_tasks_to_your_list_drag_to_reorder_them_as_needed')}
                  </Text>
                  {errors.tasks && (
                    <Banner tone="critical">{errors.tasks}</Banner>
                  )}
                </TextContainer>

                {/* Show existing tasks using SortableList if any exist */}
                {tasks.length > 0 && (
                  <Box paddingBlockStart="300">
                    <SortableList 
                      items={tasks} 
                      setItems={setTasks} 
                      onDelete={handleDeleteTask}
                      setIsDirty={setIsDirty}
                    />
                  </Box>
                )}

                {/* Task input area - shown when button is clicked */}
                {showTaskInput ? (
                  <BlockStack gap="300">
                    <TextField
                      label={t('task_title')}
                      value={newTaskTitle}
                      onChange={setNewTaskTitle}
                      autoComplete="off"
                      placeholder={t('enter_task_title')}
                      error={taskInputError}
                      autoFocus
                    />
                    <InlineStack gap="200">
                      <Button onClick={handleAddTask} variant="primary">{t('add_task')}</Button>
                      <Button onClick={() => {
                        setShowTaskInput(false);
                        setTaskInputError('');
                        setNewTaskTitle('');
                      }}>{t('cancel')}</Button>
                    </InlineStack>
                  </BlockStack>
                ) : (
                  <Box paddingBlockStart="300">
                    <Button 
                      onClick={() => setShowTaskInput(true)} 
                      icon={PlusIcon}
                      variant='primary'
                    >
                      {t('add_task')}
                    </Button>
                  </Box>
                )}
                
                {/* Empty state if no tasks */}
                {!showTaskInput && tasks.length === 0 && (
                  <Box
                    paddingBlock="400"
                    background="bg-surface-secondary"
                    borderRadius="200"
                    padding="400"
                  >
                    <Text as="p" color="subdued">
                      {t('no_tasks_added_yet_click_add_task_to_create_your_first_task')}
                    </Text>
                  </Box>
                )}
              </BlockStack>
            </Card>
          </Layout.AnnotatedSection>
        </Layout>
      </Form>
      <div style={{marginTop:"20px"}}></div>

      {isDirty && (
        <SaveBar
          id="edit-tasklist-save-bar"
          open={isDirty}
        >
          <button
            type="button"
            variant="primary"
            onClick={async () => {
              try {
                await handleSave();
                setIsDirty(false);
              } catch (err) {
                // Error handled silently
              }
            }}
          >
            {t('save')}
          </button>
          <button
            type="button"
            onClick={handleDiscard}
          >
            {t('discard')}
          </button>
        </SaveBar>
      )}

      {showToast && (
        <Frame>
        <Toast
          content={toastContent}
          error={Object.keys(errors).length > 0}
          onDismiss={() => setShowToast(false)}
          duration={5000}
        />
        </Frame>
      )}
    </Page>
  );
}